import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { displayErrorAlert, displaySuccessAlert } from 'services/alerts';

import View from './view';
import * as Actions from './actions';
import * as RosterSelectors from 'shared_redux/selectors/view/roster';
import * as GroupInviteLinksSelectors from 'shared_redux/selectors/view/group_invite_links';
import * as OrganizationsSelectors from 'shared_redux/selectors/models/organizations';
import { FETCH_GROUP_INVITE_LINKS_REQUESTED, CREATE_GROUP_INVITE_LINK_REQUESTED, DELETE_GROUP_INVITE_LINK_REQUESTED, HIDE_GROUP_INVITE_DIALOG, showGroupInviteDialog } from 'shared_redux/actions/group_invite_links';
import GroupInviteLinkView from '../group_invite';

// Create a global tracker to persist state between renders
window.InviteLinkTracker = window.InviteLinkTracker || {
    groupInviteLinks: {},
    
    setHasInviteLink(groupId, hasLink) {
        if (groupId) {
            this.groupInviteLinks[groupId] = hasLink;
            console.log(`[InviteLinkTracker] Setting group ${groupId} invite link to: ${hasLink}`);
        }
    },
    
    hasInviteLink(groupId) {
        if (!groupId) return false;
        return this.groupInviteLinks[groupId] === true;
    }
};

// For debugging
console.log('[Init] InviteLinkTracker initialized:', window.InviteLinkTracker);

const mapStateToProps = (state, props) => {
    const result = RosterSelectors.getRosterViewModel(state, props);
    document.title = result.title;
    
    // Access actual team and group data directly from the database state
    // This approach avoids any issues with parsing names that might contain hyphens


    
    // Start with group ID from the result
    let effectiveGroupId = result.groupId; // From the roster view model
    
    // URL params take precedence if available
    if (state.router && state.router.location) {
        try {
            const searchParams = new URLSearchParams(state.router.location.search);
            const groupIdFromUrl = searchParams.get('group_id');
            if (groupIdFromUrl) {
                effectiveGroupId = groupIdFromUrl;
            }
        } catch (error) {
            console.error('Error parsing URL params:', error);
        }
    }
    
    // Alias for clarity and backward compatibility
    const currentGroupId = effectiveGroupId;

    // First check our tracker to see if we already know this group has an invite link
    let hasGroupInviteLink = window.InviteLinkTracker.hasInviteLink(currentGroupId);
    
    // If we don't already know, check Redux store
    if (!hasGroupInviteLink && currentGroupId) {
        try {
            // Check existing invite links in Redux store
            if (state.group_invite_links) {
                const inviteLinks = GroupInviteLinksSelectors.getInviteLinks(state);
                // Only consider links for the current group
                hasGroupInviteLink = inviteLinks && inviteLinks.some(link => 
                    link.group_id === currentGroupId && link.is_active === true
                );
                
                // Store result in tracker for future reference
                if (hasGroupInviteLink) {
                    window.InviteLinkTracker.setHasInviteLink(currentGroupId, true);
                }
                
                console.log(`[MapState] Group ${currentGroupId} has invite link (Redux): ${hasGroupInviteLink}`);
            }
        } catch (error) {
            console.error('Error accessing group_invite_links state:', error);
        }
    }
    
    // Look up team and group names directly from state using IDs
    // Look up team and group names from normalized itemsById
    const teamObj = state.database?.Teams?.itemsById?.[result.teamId];
    const groupObj = state.database?.Groups?.itemsById?.[result.groupId];
    const teamName = teamObj ? teamObj.name : null;
    const groupName = groupObj ? groupObj.name : null;

    // Get organization data from Redux store
    const organization = OrganizationsSelectors.getOrganization(state);

    return {
        ...result,
        teamName,
        groupName,
        hasGroupInviteLink,
        currentGroupId,
        organization
    };



};

const mapDispatchToProps = (dispatch) => {
    return {
        fetchRosterSettings: () => dispatch(Actions.fetchRosterSettings()),
        fetchAthletes: (groupId, teamId, search) => dispatch(Actions.fetchAthletes(groupId, teamId, search)),
        filterAthletes: (name) => dispatch(Actions.filterAthletes(name)),
        createAthlete: (organizationId, teamId, groupId) => dispatch(Actions.createAthlete(organizationId, teamId, groupId)),
        filterColumns: () => dispatch(Actions.filterColumns()),
        importAthletes: () => dispatch(Actions.importAthletes()),
        fetchInviteLinks: (groupId) => dispatch({ type: FETCH_GROUP_INVITE_LINKS_REQUESTED, payload: { groupId } }),
        showGroupInviteDialog: (effectiveGroupId, teamId, teamName, groupName) => {
            // Use the provided effective group ID, or fallback to URL parameter
            let groupId = effectiveGroupId;
            if (!groupId) {
                const urlParams = new URLSearchParams(window.location.search);
                groupId = urlParams.get('group_id');
            }
            
            console.log('[Action] Showing invite dialog with:', { groupId, teamId, teamName, groupName });
            dispatch(showGroupInviteDialog(groupId, teamId, teamName, groupName));
        },
        copyGroupInviteLink: () => {
            // Access to getState needs to be in a thunk function
            return (dispatch, getState) => {
                try {
                    // Directly get group ID from URL parameters
                    const urlParams = new URLSearchParams(window.location.search);
                    const groupId = urlParams.get('group_id');
                    
                    console.log('[Action] Copying invite link for group:', groupId);
                    
                    if (!groupId) {
                        displayErrorAlert('Could not determine which group to copy invite link for');
                        return;
                    }
                    
                    // Get invite links from state
                    const state = getState();
                    const inviteLinks = GroupInviteLinksSelectors.getInviteLinks(state);
                    const groupInviteLink = inviteLinks.find(link => 
                        link.group_id === groupId && link.is_active === true
                    );
                    
                    if (groupInviteLink && groupInviteLink.invite_code) {
                        const baseUrl = process.env.NODE_ENV === 'production' ?
                            "https://app.reponestrength.com/" : 
                            "http://localhost:3001/";
                        const fullUrl = baseUrl + 'group-invite/' + groupInviteLink.invite_code;
                        
                        navigator.clipboard.writeText(fullUrl)
                            .then(() => displaySuccessAlert('Invite link copied to clipboard'))
                            .catch(err => {
                                console.error('Could not copy text: ', err);
                                displayErrorAlert('Failed to copy invite link');
                            });
                    } else {
                        displayErrorAlert('No active invite link found for this group');
                    }
                } catch (error) {
                    console.error('Error copying invite link:', error);
                    displayErrorAlert('Failed to copy invite link');
                }
            };
        }
    };
};

const Container = connect(
    mapStateToProps,
    mapDispatchToProps
)(View);

const mapStateToPropsForInvite = (state, ownProps) => {
    // Try to find team and group information from various sources
    // First check if props were explicitly passed to the component
    let teamId = ownProps.teamId || null;
    let teamName = ownProps.teamName || null;
    let groupId = ownProps.groupId || null;
    let groupName = ownProps.groupName || null;
    
    // If we don't have a groupId yet, check router state
    if (!groupId && state.router?.location?.search) {
        const params = new URLSearchParams(state.router.location.search);
        groupId = params.get('group_id');
    }
    
    // Extract team and group info - using let since we might update these
    let rosterViewModel = RosterSelectors.getRosterViewModel(state);
    teamId = teamId || rosterViewModel.teamId;
    groupId = groupId || rosterViewModel.groupId;
    if (teamName == null) teamName = rosterViewModel.teamName || null;
    if (groupName == null) groupName = rosterViewModel.groupTitle || null;
    
    // If the view model doesn't have the names, try to get them from the database
    if ((teamName == null) && teamId && state.database?.Teams?.items?.[teamId]) {
        teamName = state.database.Teams.items[teamId].name;
    }
    
    if ((groupName == null) && groupId && state.database?.Groups?.items?.[groupId]) {
        groupName = state.database.Groups.items[groupId].name;
    }
    
    // Extract directly from URL search params (the most reliable method)
    let groupIdFromSearch = null;
    
    // Safely check for router state
    if (state.router?.location?.search) {
        try {
            const searchParams = new URLSearchParams(state.router.location.search);
            groupIdFromSearch = searchParams.get('group_id');
        } catch (error) {
            // Silently handle error and continue with null groupIdFromSearch
            console.debug('Error parsing URL search params:', error);
        }
    }
    
    // Determine effectiveGroupId: prefer groupId from URL, else resolved groupId
    let effectiveGroupId = groupIdFromSearch || groupId;
    
    // Get the actual team and group names based on the identified group ID
    if (effectiveGroupId && state.database?.Groups?.items?.[effectiveGroupId]) {
        // Get the group name directly from database state
        const group = state.database.Groups.items[effectiveGroupId];
        groupName = group.name;
        
        // Get the team for this group
        if (group.team_id && state.database?.Teams?.items?.[group.team_id]) {
            const team = state.database.Teams.items[group.team_id];
            teamName = team.name;
        }
    } else if (state.router?.location?.search) {
        // Try to get team ID from URL if not viewing a group
        try {
            const searchParams = new URLSearchParams(state.router.location.search);
            const urlTeamId = searchParams.get('team_id');
            
            if (urlTeamId && state.database?.Teams?.items?.[urlTeamId]) {
                // If just viewing a team (no group selected)
                const team = state.database.Teams.items[urlTeamId];
                teamName = team.name;
            }
        } catch (error) {
            console.error('Error parsing URL for team ID:', error);
        }
    }
    
    // At this point, we should have the best available data for team and group names
    
    // Get the dialog visibility state
    const groupInviteLinksState = state.group_invite_links || {};
    
    return {
        teamId,
        teamName,
        groupId,
        groupName,
        isShowing: groupInviteLinksState.isShowingDialog,
        inviteLinks: groupInviteLinksState.inviteLinks || []
    };
};

const createImportAction = type => payload => ({
    type,
    payload
});

const mapDispatchToPropsForInvite = dispatch => ({
    onClose: () => dispatch(createImportAction(HIDE_GROUP_INVITE_DIALOG)()),
    saveInviteLink: data => dispatch(createImportAction(CREATE_GROUP_INVITE_LINK_REQUESTED)(data)),
    removeInviteLink: linkId => dispatch(createImportAction(DELETE_GROUP_INVITE_LINK_REQUESTED)(linkId)),
    fetchInviteLinks: groupId => dispatch(createImportAction(FETCH_GROUP_INVITE_LINKS_REQUESTED)({ groupId }))
});

const ConnectedGroupInviteLink = connect(
    mapStateToPropsForInvite,
    mapDispatchToPropsForInvite
)(GroupInviteLinkView);

// Export both the main component and the connected GroupInviteLink
const ExportContainer = withRouter(Container);
ExportContainer.GroupInviteLink = ConnectedGroupInviteLink;

export default ExportContainer;
